import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { UserRole } from "@/utils/roleManager";

export interface ApiKey {
  id: number;
  customerId: number;
  createdBy?: string;
  value?: string | null;
  name: string;
  role: UserRole;
  createdAt?: string;
  validUntil?: string | null;
  active: boolean;
  lastUsed?: string | null;
}

export interface ApiKeyCreateRequest {
  customerId: number;
  name: string;
  role: UserRole;
  validUntil?: string | null;
}

export interface ApiKeyUpdateRequest {
  id: number;
  active?: boolean;
  validUntil?: string | null;
}

export interface ApiKeyResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class ApiKeyService extends BaseApiService {
  private static instance: ApiKeyService;

  public static getInstance(): ApiKeyService {
    if (!ApiKeyService.instance) {
      ApiKeyService.instance = new ApiKeyService();
    }
    return ApiKeyService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getAllApiKeys(): Promise<ApiKeyResponse<ApiKey[]>> {
    try {
      const response = await this.get<{ data: ApiKey[] }>("/apiToken", this.getCoreBaseUrl());
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : "Failed to fetch API keys",
      };
    }
  }

  public async createApiKey(request: ApiKeyCreateRequest): Promise<ApiKeyResponse<ApiKey | null>> {
    try {
      const response = await this.post<{ data: ApiKey }>(
        "/apiToken",
        request,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : "Failed to create API key",
      };
    }
  }

  public async updateApiKey(request: ApiKeyUpdateRequest): Promise<ApiKeyResponse<ApiKey | null>> {
    try {
      const response = await this.put<{ data: ApiKey }>(
        "/apiToken",
        request,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : "Failed to update API key",
      };
    }
  }
}
